import {
  ArrowPathIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  HomeIcon,
  UserGroupIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { lazy, LazyExoticComponent, ComponentType } from "react";

// --- LAZY LOAD PAGES ---
const AnalyticsPage = lazy(() => import("../pages/AnalyticsPage"));
const DashboardPage = lazy(() => import("../pages/DashboardPage"));
const DentalCalendarPage = lazy(() => import("../pages/DentalCalendarPage"));

const LoginPage = lazy(() => import("../pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("../pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/ResetPasswordPage"));
const OnboardingPage = lazy(() => import("../pages/OnboardingPage"));
const OrganisationSettingsPage = lazy(
  () => import("../pages/OrganisationSettingsPage")
);
const AcceptInvitePage = lazy(() => import("../pages/AcceptInvitePage"));
const PatientOverviewPage = lazy(() => import("../pages/PatientOverviewPage"));
const RecallsPage = lazy(() => import("../pages/RecallsPage"));
const SelectFormTemplatePage = lazy(
  () => import("../pages/SelectFormTemplatePage")
);
const SettingsPage = lazy(() => import("../pages/SettingsPage"));
const SignUpPage = lazy(() => import("../pages/SignUpPage"));
const TeamHubPage = lazy(() => import("../pages/TeamHubPage"));
const TreatmentsPage = lazy(() => import("../pages/TreatmentsPage"));
const PatientsPage = lazy(() => import("../pages/PatientsPage"));
const PatientDetailPage = lazy(() => import("../pages/PatientDetailPage"));
const PatientEditPage = lazy(() => import("../pages/PatientEditPage"));
const PatientCreatePage = lazy(() => import("../pages/PatientCreatePage"));
const PatientMergePage = lazy(() => import("../pages/PatientMergePage"));
const PublicBookingPage = lazy(() => import("../pages/PublicBookingPage"));
const PublicPeriodontalChartPage = lazy(() => import("../pages/PublicPeriodontalChartPage"));
const PublicTreatmentsCatalogPage = lazy(
  () => import("../pages/PublicTreatmentsCatalogPage")
);
const PublicReviewWidgetPage = lazy(
  () => import("../pages/PublicReviewWidgetPage")
);
const PatientPortalPage = lazy(() => import("../pages/PatientPortalPage"));
const PortalMedicalFormPage = lazy(() => import("../pages/PortalMedicalFormPage"));
const PortalReschedulePage = lazy(() => import("../pages/PortalReschedulePage"));
const RecallBookingPage = lazy(() => import("../pages/RecallBookingPage"));
const PatientFilePage = lazy(() => import("../pages/PatientFilePage"));
const PatientDocumentUploadPage = lazy(
  () => import("../pages/PatientDocumentUploadPage")
);
const DataPage = lazy(() => import("../pages/DataPage"));
const InvoiceDetailPage = lazy(() => import("../pages/InvoiceDetailPage"));
const BillingReviewPage = lazy(() => import("../pages/BillingReviewPage"));
const InsurersPage = lazy(() => import("../pages/InsurersPage"));



// --- ROUTE DEFINITION ---

export interface RouteDefinition {
  path: string;
  name: string;
  icon: ComponentType<{ className?: string }>;
  component: LazyExoticComponent<ComponentType<any>>;
  inSidebar: boolean;
  isDentalOnly?: boolean;
  children?: RouteDefinition[];
}

export const appRoutes: RouteDefinition[] = [
  // --- Sidebar Visible Routes ---
  {
    path: "/",
    name: "Dashboard",
    icon: HomeIcon,
    component: DashboardPage,
    inSidebar: true,
  },

  {
    path: "/calendar",
    name: "Calendar",
    icon: CalendarDaysIcon,
    component: DentalCalendarPage,
    inSidebar: true,
  },

  {
    path: "/patients",
    name: "Patients",
    icon: UserGroupIcon,
    component: PatientsPage,
    inSidebar: true,
  },
  {
    path: "/patients/new",
    name: "New Patient",
    icon: () => null,
    component: PatientCreatePage,
    inSidebar: false,
  },
  {
    path: "/patients/merge",
    name: "Merge Patients",
    icon: () => null,
    component: PatientMergePage,
    inSidebar: false,
  },
  {
    path: "/patients/:patientId",
    name: "Patient Detail",
    icon: () => null,
    component: PatientDetailPage,
    inSidebar: false,
  },
  {
    path: "/patients/:patientId/edit",
    name: "Edit Patient",
    icon: () => null,
    component: PatientEditPage,
    inSidebar: false,
  },
  {
    path: "/recalls",
    name: "Recalls",
    icon: ArrowPathIcon,
    component: RecallsPage,
    inSidebar: true,
  },
  {
    path: "/team-hub",
    name: "Team Hub",
    icon: UserGroupIcon,
    component: TeamHubPage,
    inSidebar: true,
  },
  {
    path: "/treatments",
    name: "Treatments",
    icon: ClipboardDocumentListIcon,
    component: TreatmentsPage,
    inSidebar: true,
  },
  {
    path: "/treatments/:tab",
    name: "Treatments Tab",
    icon: () => null,
    component: TreatmentsPage,
    inSidebar: false,
  },
  {
    path: "/data",
    name: "Data",
    icon: TableCellsIcon,
    component: DataPage,
    inSidebar: true,
  },
  {
    path: "/billing-review",
    name: "Billing Review",
    icon: ChartBarIcon,
    component: BillingReviewPage,
    inSidebar: true,
  },
  {
    path: "/organisation-settings",
    name: "Organisation Settings",
    icon: BuildingOfficeIcon,
    component: OrganisationSettingsPage,
    inSidebar: true,
  },
  {
    path: "/organisation-settings/:tab",
    name: "Organisation Settings Tab",
    icon: () => null,
    component: OrganisationSettingsPage,
    inSidebar: false,
  },

  {
    path: "/insurers",
    name: "Insurers",
    icon: BuildingOfficeIcon,
    component: InsurersPage,
    inSidebar: false, // Access via organisation settings
  },

  {
    path: "/settings",
    name: "Settings",
    icon: Cog6ToothIcon,
    component: SettingsPage,
    inSidebar: false, // Removed from sidebar navigation
  },

  // --- Non-Sidebar Routes (for routing purposes only) ---

  // Internal analytics dashboard (accessible via direct URL only)
  {
    path: "/analytics",
    name: "Analytics",
    icon: ChartBarIcon,
    component: AnalyticsPage,
    inSidebar: false,
  },
  
  // Public booking routes (no authentication required)
  {
    path: "/booking",
    name: "Public Booking",
    icon: () => null,
    component: PublicBookingPage,
    inSidebar: false,
  },
  {
    path: "/booking/:practiceId",
    name: "Practice Booking",
    icon: () => null,
    component: PublicBookingPage,
    inSidebar: false,
  },
  {
    path: "/patient/:token",
    name: "Patient Portal",
    icon: () => null,
    component: PatientPortalPage,
    inSidebar: false,
  },
  {
    path: "/patient/:token/form/:formId",
    name: "Portal Medical Form",
    icon: () => null,
    component: PortalMedicalFormPage,
    inSidebar: false,
  },
  {
    path: "/patient/:token/reschedule",
    name: "Portal Reschedule",
    icon: () => null,
    component: PortalReschedulePage,
    inSidebar: false,
  },
  {
    path: "/r/:token",
    name: "Recall Booking",
    icon: () => null,
    component: RecallBookingPage,
    inSidebar: false,
  },
  
  // Public periodontal chart tool (no authentication required)
  {
    path: "/periochart",
    name: "Periodontal Chart Tool",
    icon: () => null,
    component: PublicPeriodontalChartPage,
    inSidebar: false,
  },
  {
    path: "/codes-catalog",
    name: "Treatment Catalog",
    icon: () => null,
    component: PublicTreatmentsCatalogPage,
    inSidebar: false,
  },
  {
    path: "/reviews",
    name: "Review Widget",
    icon: () => null,
    component: PublicReviewWidgetPage,
    inSidebar: false,
  },
  {
    path: "/upload/:token",
    name: "Patient Document Upload",
    icon: () => null,
    component: PatientDocumentUploadPage,
    inSidebar: false,
  },
  
  {
    path: "/login",
    name: "Login",
    icon: () => null,
    component: LoginPage,
    inSidebar: false,
  },
  {
    path: "/forgot-password",
    name: "Forgot Password",
    icon: () => null,
    component: ForgotPasswordPage,
    inSidebar: false,
  },
  {
    path: "/reset-password",
    name: "Reset Password",
    icon: () => null,
    component: ResetPasswordPage,
    inSidebar: false,
  },
  {
    path: "/signup",
    name: "Sign Up",
    icon: () => null,
    component: SignUpPage,
    inSidebar: false,
  },
  {
    path: "/accept-invite",
    name: "Accept Invite",
    icon: () => null,
    component: AcceptInvitePage,
    inSidebar: false,
  },
  {
    path: "/onboarding",
    name: "Onboarding",
    icon: () => null,
    component: OnboardingPage,
    inSidebar: false,
  },
  {
    path: "/patient/:patientId/overview",
    name: "Patient Overview",
    icon: () => null,
    component: PatientOverviewPage,
    inSidebar: false,
  },
  {
    path: "/patient/:patientId/file",
    name: "Patient File",
    icon: () => null,
    component: PatientFilePage,
    inSidebar: false,
  },
  {
    path: "/patient/:patientId/file/:tab",
    name: "Patient File Tab",
    icon: () => null,
    component: PatientFilePage,
    inSidebar: false,
  },
  // --- Medical Forms ---
  {
    path: "/select-form-template",
    name: "Select Form Template",
    icon: () => null,
    component: SelectFormTemplatePage,
    inSidebar: false,
  },
  // --- Invoice Detail ---
  {
    path: "/invoice/:invoiceId",
    name: "Invoice Detail",
    icon: () => null,
    component: InvoiceDetailPage,
    inSidebar: false,
  },
];
